import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { productsApi, type ApiListResponse } from '@/lib/api'
import { useNotificationStore } from './notifications'
import type { Product } from '@/types'

export const useProductsStore = defineStore('products', () => {
  const products = ref<Product[]>([])
  const currentProduct = ref<Product | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<{
    page: number
    pageSize: number
    total: number
    hasNext: boolean
  } | null>(null)

  const notifications = useNotificationStore()

  // Computed
  const productsCount = computed(() => products.value.length)

  const activeProducts = computed(() =>
    products.value.filter(p => p.status === 'active')
  )

  const productsByCategory = computed(() => {
    const grouped: Record<string, Product[]> = {}
    products.value.forEach(product => {
      const category = product.category || 'Uncategorized'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(product)
    })
    return grouped
  })

  // Actions
  async function fetchProducts(params?: any): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response: ApiListResponse<Product> = await productsApi.list(params)
      products.value = response.data
      meta.value = response.meta
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch products'
      notifications.error('Failed to load products')
    } finally {
      loading.value = false
    }
  }

  async function fetchProduct(id: string): Promise<Product | null> {
    loading.value = true
    error.value = null

    try {
      const response = await productsApi.get(id)
      const product = response.data
      currentProduct.value = product

      // Update product in store if it exists
      const index = products.value.findIndex(p => p._id === id)
      if (index !== -1) {
        products.value[index] = product
      } else {
        products.value.push(product)
      }

      return product
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch product'
      notifications.error('Failed to load product')
      return null
    } finally {
      loading.value = false
    }
  }

  async function createProduct(productData: Partial<Product>): Promise<Product | null> {
    loading.value = true
    error.value = null

    try {
      const response = await productsApi.create(productData)
      const newProduct = response.data
      products.value.push(newProduct)
      notifications.success('Product created successfully')
      return newProduct
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to create product'
      notifications.error(err.response?.data?.message || 'Failed to create product')
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateProduct(id: string, productData: Partial<Product>): Promise<Product | null> {
    loading.value = true
    error.value = null

    try {
      const response = await productsApi.update(id, productData)
      const updatedProduct = response.data

      const index = products.value.findIndex(p => p._id === id)
      if (index !== -1) {
        products.value[index] = updatedProduct
      }

      if (currentProduct.value?._id === id) {
        currentProduct.value = updatedProduct
      }

      notifications.success('Product updated successfully')
      return updatedProduct
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to update product'
      notifications.error(err.response?.data?.message || 'Failed to update product')
      return null
    } finally {
      loading.value = false
    }
  }

  async function deleteProduct(id: string): Promise<boolean> {
    loading.value = true
    error.value = null

    try {
      await productsApi.delete(id)
      products.value = products.value.filter(p => p._id !== id)
      if (currentProduct.value?._id === id) {
        currentProduct.value = null
      }
      notifications.success('Product deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete product'
      notifications.error(err.response?.data?.message || 'Failed to delete product')
      return false
    } finally {
      loading.value = false
    }
  }

  function getProductById(id: string): Product | undefined {
    return products.value.find(p => p._id === id)
  }

  function clearProducts(): void {
    products.value = []
    currentProduct.value = null
    meta.value = null
    error.value = null
  }

  return {
    // State
    products,
    currentProduct,
    loading,
    error,
    meta,

    // Computed
    productsCount,
    activeProducts,
    productsByCategory,

    // Actions
    fetchProducts,
    fetchProduct,
    createProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    clearProducts,
  }
})
